import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import axios from 'axios';
import { API } from '../config';
import { trackGoal } from '../lib/metrika';

const GREETING = 'Здравствуйте. Я Аня AI, помощник Светланы. Могу рассказать о том, как проходят консультации, или просто поговорить. Что вас привело?';

// Скорость «печати» ответа — около 50ms/символ (темп спокойного разговора).
const TYPE_SPEED_MS = 50;

export default function AnyaWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);     // {role, content}
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);  // ждём ответ от backend
  const [typingText, setTypingText] = useState(''); // частично «напечатанный» текст последнего ответа Ани
  const [sessionId, setSessionId] = useState(null);
  const [pulse, setPulse] = useState(true);

  const listRef = useRef(null);
  const messagesRef = useRef(messages);
  const sessionIdRef = useRef(null);
  const summarySentRef = useRef(false);
  const typeTimerRef = useRef(null);

  useEffect(() => { messagesRef.current = messages; }, [messages]);
  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);

  // Приветствие при первом открытии
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: 'assistant', content: GREETING }]);
    }
  }, [open, messages.length]);

  // Автоскролл
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, typingText, thinking, open]);

  // Снять пульсацию через несколько секунд
  useEffect(() => {
    const t = setTimeout(() => setPulse(false), 5000);
    return () => clearTimeout(t);
  }, []);

  // Очистка таймеров при размонтировании
  useEffect(() => () => { if (typeTimerRef.current) clearInterval(typeTimerRef.current); }, []);

  // Отправляем summary в Telegram при закрытии чата / уходе со страницы
  const sendSummary = () => {
    const hist = messagesRef.current;
    const userMsgs = hist.filter((m) => m.role === 'user' && m.content.trim());
    if (userMsgs.length < 1 || summarySentRef.current) return;
    summarySentRef.current = true;
    try {
      // sendBeacon переживёт закрытие вкладки, fetch — может не успеть
      const payload = JSON.stringify({ session_id: sessionIdRef.current, history: hist });
      const blob = new Blob([payload], { type: 'application/json' });
      if (navigator.sendBeacon) {
        navigator.sendBeacon(`${API}/chat/summary`, blob);
      } else {
        fetch(`${API}/chat/summary`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: payload,
          keepalive: true,
        }).catch(() => {});
      }
    } catch {
      /* молча — пользователь уже уходит */
    }
  };

  // beforeunload: отправляем summary если закрыли вкладку с открытым чатом
  useEffect(() => {
    const onUnload = () => sendSummary();
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, []);

  const closeChat = () => {
    sendSummary();
    setOpen(false);
  };

  // Анимация «печати» — добавляем символы с задержкой
  const typeOut = (fullText, onDone) => {
    if (typeTimerRef.current) clearInterval(typeTimerRef.current);
    setTypingText('');
    let i = 0;
    typeTimerRef.current = setInterval(() => {
      i += 1;
      setTypingText(fullText.slice(0, i));
      if (i >= fullText.length) {
        clearInterval(typeTimerRef.current);
        typeTimerRef.current = null;
        setTypingText('');
        setMessages((m) => [...m, { role: 'assistant', content: fullText }]);
        onDone?.();
      }
    }, TYPE_SPEED_MS);
  };

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || thinking || typingText) return;
    setInput('');
    const next = [...messages, { role: 'user', content: text }];
    // Цель Метрики: первый отправленный вопрос Ане (не каждое сообщение — только первое в сессии)
    const isFirstUserMsg = !messages.some((m) => m.role === 'user');
    if (isFirstUserMsg) trackGoal('anya-first-message');
    setMessages(next);
    setThinking(true);

    try {
      // в history передаём всё кроме первого приветствия и последнего user (он идёт в message)
      const history = next.slice(0, -1).filter((m) => !(m.role === 'assistant' && m.content === GREETING));
      const { data } = await axios.post(`${API}/chat`, {
        session_id: sessionIdRef.current,
        message: text,
        history,
      });
      if (data.session_id && !sessionIdRef.current) {
        setSessionId(data.session_id);
      }
      setThinking(false);
      typeOut(data.reply || '...');
    } catch (err) {
      setThinking(false);
      const fallback = 'Что-то у меня не получилось ответить сейчас. Если важно — напишите Светлане через форму ниже, она обязательно ответит.';
      typeOut(fallback);
    }
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => { setOpen(true); setPulse(false); trackGoal('anya-opened'); }}
          className={`fixed z-50 right-4 md:right-6 bottom-4 md:bottom-6 w-14 h-14 rounded-full bg-wine text-cream shadow-warm flex items-center justify-center hover:bg-wineHover transition-colors ${pulse ? 'animate-gentle-pulse' : ''}`}
          aria-label="Открыть чат с Аней AI"
          data-testid="anya-toggle"
        >
          <MessageCircle size={26} strokeWidth={1.8} />
        </button>
      )}

      {/* Chat window */}
      {open && (
        <div
          className="fixed z-50 inset-x-0 bottom-0 md:inset-auto md:right-6 md:bottom-6 md:w-[380px] md:h-[540px] flex flex-col bg-cream border border-ink/15 shadow-warm overflow-hidden animate-fade-up"
          data-testid="anya-window"
        >
          {/* Header */}
          <div className="bg-wine text-cream px-5 py-3.5 flex items-center justify-between">
            <div>
              <div className="font-heading text-lg leading-tight" data-testid="anya-widget-title">Аня AI</div>
              <div className="text-xs opacity-80">помощник Светланы</div>
            </div>
            <button
              onClick={closeChat}
              aria-label="Закрыть"
              className="p-1 hover:bg-wineHover transition-colors"
              data-testid="anya-close"
            >
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={listRef}
            className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-cream"
            data-testid="anya-messages"
          >
            {messages.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[82%] px-3.5 py-2.5 text-[14.5px] leading-relaxed ${
                    m.role === 'user'
                      ? 'bg-wine text-cream rounded-2xl rounded-br-md'
                      : 'bg-oat text-ink rounded-2xl rounded-bl-md'
                  }`}
                  data-testid={`anya-msg-${m.role}-${i}`}
                >
                  {m.content}
                </div>
              </div>
            ))}

            {/* Текущая «печатаемая» реплика Ани */}
            {typingText && (
              <div className="flex justify-start" data-testid="anya-typing-text">
                <div className="max-w-[82%] px-3.5 py-2.5 text-[14.5px] leading-relaxed bg-oat text-ink rounded-2xl rounded-bl-md">
                  {typingText}
                  <span className="inline-block w-[2px] h-[1em] bg-wine/70 align-middle ml-0.5 animate-pulse" />
                </div>
              </div>
            )}

            {/* Точки «думает», пока ждём ответ от backend */}
            {thinking && (
              <div className="flex justify-start" data-testid="anya-thinking">
                <div className="bg-oat rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-1.5">
                  <span className="w-2 h-2 bg-wine rounded-full animate-dot-1" />
                  <span className="w-2 h-2 bg-wine rounded-full animate-dot-2" />
                  <span className="w-2 h-2 bg-wine rounded-full animate-dot-3" />
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <form onSubmit={send} className="border-t border-ink/10 p-3 bg-oat/40 flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Напишите мне..."
              className="flex-1 bg-cream border border-ink/15 rounded-full px-4 py-2.5 text-[14.5px] text-ink placeholder-ink/40 focus:outline-none focus:border-wine"
              data-testid="anya-input"
            />
            <button
              type="submit"
              disabled={!input.trim() || thinking || !!typingText}
              className="w-10 h-10 rounded-full bg-wine text-cream flex items-center justify-center hover:bg-wineHover transition-colors disabled:opacity-40"
              aria-label="Отправить"
              data-testid="anya-send"
            >
              <Send size={18} />
            </button>
          </form>

          <div className="text-[10px] text-ink/50 text-center pb-1.5 bg-oat/40">
            Помощник сайта. Не заменяет консультацию
          </div>
        </div>
      )}
    </>
  );
}
